import { Component } from '@angular/core';
import { clientLegalName, getAssociationText } from '@core/util/client';
import { capitalize } from '@core/util/string';
import {
	AssociatedContactType,
	BusinessClient,
	PersonClient,
} from '@models/clients';
import {
	Brokerage,
	BusinessMatterClientWithAssociations,
	MatterFileType,
	PersonMatterClientWithAssociations,
} from '@models/matter';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { MatterClients } from '@state/global/matter_clients';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { DropdownResult } from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { ICON } from '@core/util/icon';
import { NavigationService } from 'src/app/shared/services/nav/nav.service';
import { UrlService } from 'src/app/shared/services/url/url.service';
import { BusinessState } from '@state/business';
import { NameFormattingSettings } from '@models/businessSettings/settings';
import { GlobalProfileComponent } from 'src/app/private/global-profile/global-profile.component';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import { TestHooks } from './matter-search.hooks';

const enum Page {
	PROPERTY_DETAILS = 'propertyDetails',
	COMPLIANCE = 'compliance',
	FIRE_INSURANCE = 'fireInsurance',
	CONDO_TAB = 'condoTab',
	OVERVIEW = 'overview',
	ACCOUNT = 'account',
	ADJUSTMENTS = 'adjustments',
	TITLE_INSURANCE = 'titleInsurance',
	PARTIES = 'parties',
	OTHER_PARTIES = 'otherParties',
	COMMISSION = 'commission',
	BILLS_DEBTS = 'billsDebts',
	TRUST_LEDGER = 'trustLedger',
	APS = 'aps',
	ENCUMBRANCE = 'encumbrance',
	UNDERTAKINGS = 'undertakings',
	RESPONSE_TO_REQUISITIONS = 'responseToReq',
	DOCUMENTS = 'documents',
	REDIRECTION = 'redirection',
	HOLDBACKS = 'holdbacks',
}

const pageTerms = {
	[Page.PROPERTY_DETAILS]: [
		'property details',
		'jurisdiction',
		'lro',
		'land registry  office',
		'legal',
		'legal description',
		'land titles',
		'address',
		'interest',
		'fee simple',
		'leasehold',
		'survey',
		'lockbox code',
		'unit',
		'lot',
		'parcel',
		'section',
		'municipality',
		'easement',
		'right of way',
		'city',
		'property number',
		'roll number',
		'builder number',
		'unit type',
		'condo plan',
		'level number',
		'mobile home',
		'tenanted',
		'parcel of tied land',
		'POTL',
		'resale',
		'commercial',
		'property type',
	],
	[Page.COMPLIANCE]: [
		'compliance',
		'off title',
		'tax',
		'zoning',
		'gas',
		'building',
		'fire',
		'utility',
		'department',
		'search',
		'water',
	],
	[Page.FIRE_INSURANCE]: [
		'policy',
		'fire insurance',
		'company',
		'binder',
		'guarantee',
		'coverage',
		'expiry',
	],
	[Page.CONDO_TAB]: [
		'condominium',
		'management',
		'status',
		'certificate',
		'status certificate fee',
		'self managed',
		'attention',
		'condo corporation',
		'fee',
	],
	[Page.OVERVIEW]: [
		'overview',
		'appointment',
		'requisition date',
		'map',
		'checklist',
		'notes',
		'occupancy date',
		'file number',
		'execution date',
		'witness',
		'commissioner',
		'dates',
	],
	[Page.ACCOUNT]: [
		'statement of account',
		'fee',
		'cost',
		'disbursements',
		'conveyancing',
		'price',
		'inclusion',
		'closer fees',
		'photocopies',
		'postage',
		'fax transmission',
		'couriers',
		'levy',
	],
	[Page.ADJUSTMENTS]: [
		'statement of adjustments',
		'sale',
		'price',
		'deposit',
		'taxes',
		'property taxes',
		'land taxes',
		'business taxes',
		'realty taxes',
		'interim',
		'previous',
		'estimated',
		'common expenses',
		'occupancy',
		'rent',
		'prepaid',
		'tenancy',
		'heat',
		'chattels',
		'considerations',
		'warranty',
		'electric',
		'gas',
		'rental period',
		'propane',
		'oil',
		'gallons',
		'litres',
		'carbon tax',
		'capacity',
		'tank',
		'tarion',
		'enrolment',
		'hcra',
	],
	[Page.TITLE_INSURANCE]: [
		'premium',
		'insurable amount',
		'order',
		'expire',
		'homeowner',
		'lender',
		'policy',
		'title',
		'issue',
	],
	[Page.PARTIES]: [
		'clients',
		'salutations',
		'fla',
		'family law act',
		'service address',
		'report address',
		'spousal status',
		'global profile',
		'contact',
	],
	[Page.OTHER_PARTIES]: ['solicitor', 'brokerage', 'broker', 'buyer'],
	[Page.COMMISSION]: [
		'commission',
		'brokerage',
		'broker',
		'deposit',
		'deposit holder',
	],
	[Page.BILLS_DEBTS]: ['bill', 'debt', 'payment'],
	[Page.TRUST_LEDGER]: [
		'trust ledger',
		'receipts',
		'paid to you',
		'disbursements',
		'proceeds',
		'split',
		'payments',
	],
	[Page.APS]: ['deposit holder', 'agreement date', 'deposit'],
	[Page.ENCUMBRANCE]: [
		'encumbrance',
		'payout',
		'discharge',
		'mortgage',
		'loan number',
		'instruments',
		'registration date',
		'registration number',
		'per diem',
		'cheque',
		'principal',
		'interest',
	],
	[Page.UNDERTAKINGS]: ['undertaking', 'follow up'],
	[Page.HOLDBACKS]: ['construction', 'holdback', 'release', 'partial'],
	[Page.REDIRECTION]: ['redirection'],
	[Page.DOCUMENTS]: ['documents'],
	[Page.RESPONSE_TO_REQUISITIONS]: ['response', 'requisition'],
};

const PageLabels = {
	[Page.ACCOUNT]: 'Statement of accounts',
	[Page.ADJUSTMENTS]: 'Statement of adjustments',
	[Page.COMPLIANCE]: 'Compliance letters',
	[Page.CONDO_TAB]: 'Condominium',
	[Page.FIRE_INSURANCE]: 'Fire insurance',
	[Page.OVERVIEW]: 'Matter overview',
	[Page.PROPERTY_DETAILS]: 'Property details',
	[Page.TITLE_INSURANCE]: 'Title Insurance',
	[Page.BILLS_DEBTS]: 'Bills and Debts',
	[Page.OTHER_PARTIES]: 'Other parties',
	[Page.PARTIES]: 'Parties',
	[Page.COMMISSION]: 'Commission',
	[Page.TRUST_LEDGER]: 'Trust ledger',
	[Page.RESPONSE_TO_REQUISITIONS]: 'Response to requisitions',
	[Page.DOCUMENTS]: 'Documents',
	[Page.REDIRECTION]: 'Redirection',
	[Page.HOLDBACKS]: 'Holdbacks',
	[Page.ENCUMBRANCE]: 'Encumbrances',
	[Page.APS]: 'APS',
	[Page.UNDERTAKINGS]: 'Undertakings',
};

interface PageSearchElement {
	pageName: string;
	match: string;
	url: string;
}

enum ContactType {
	CLIENT = 'client',
	OTHER_PARTY_BROKERAGE = 'otherPartyBrokerage',
	BROKERAGE = 'brokerage',
	OTHER_PARTY_LAWFIRM = 'otherPartyLawfirm',
}

interface ContactsSearchElement {
	name: string;
	id?: string;
	type: ContactType;
	association: string;
}

enum SelectOption {
	PAGE = 'page',
	CONTACT = 'contact',
	DOCUMENT = 'document',
}

@Component({
	selector: 'app-matter-search',
	templateUrl: './matter-search.component.html',
	styleUrls: ['./matter-search.component.scss'],
	standalone: false,
})
export class MatterSearchComponent {
	testHooks = TestHooks;
	private pageTerms = pageTerms;
	public pageResults: PageSearchElement[] = [];
	public selectOptions = SelectOption;
	public ICON = ICON;
	public contactSearchResults: ContactsSearchElement[] = [];

	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@SelectSnapshot(SelectedMatter.matterFileType) fileType: MatterFileType;
	@SelectSnapshot(MatterClients.get_matterClientsWithAssociationMapped)
	clients: (
		| PersonMatterClientWithAssociations
		| BusinessMatterClientWithAssociations
	)[];

	@SelectSnapshot(SelectedMatter.clientBrokerage) clientBrokerage: Brokerage;
	@SelectSnapshot(SelectedMatter.otherPartyBrokerage)
	otherPartyBrokerage: Brokerage;
	@SelectSnapshot(BusinessState.getNameFormattingSettings)
	nameFormattingSettings: NameFormattingSettings;

	constructor(
		private navService: NavigationService,
		private drawerService: DrawerService,
	) {}

	onSearch(searchTerm): void {
		const lowerSearchTerm = searchTerm.toLowerCase();
		type PageOption = { [key in Page]?: string[] };
		const pagematches: PageOption = {};

		Object.entries(this.pageTerms).forEach(([key, value]) => {
			value.forEach((element) => {
				if (element.includes(lowerSearchTerm)) {
					pagematches[key] = pagematches[key]
						? [...pagematches[key], capitalize(element)]
						: [capitalize(element)];
				}
			});
		});

		this.pageResults = Object.entries(pagematches).map(([key, value]) => {
			const data: PageSearchElement = {
				pageName: PageLabels[key],
				match: value.join(', '),
				url: this.getUrl(key as Page),
			};

			return data;
		});

		this.contactSearchResults = [];
		this.clients.forEach((x) => {
			if (this.matchesName(lowerSearchTerm, x)) {
				this.contactSearchResults.push({
					id: x.id,
					name: clientLegalName(x, this.nameFormattingSettings),
					association: 'Client',
					type: ContactType.CLIENT,
				});
			}

			x.clientAssociations.forEach((y) => {
				if (this.matchesName(lowerSearchTerm, y.client)) {
					this.contactSearchResults.push({
						id: y.client.id,
						name: clientLegalName(y.client, this.nameFormattingSettings),
						association: `${clientLegalName(x, this.nameFormattingSettings)}: ${Object.entries(
							y.associations.types,
						)
							.map(([key]) => getAssociationText(key as AssociatedContactType))
							.join(', ')}`,
						type: ContactType.CLIENT,
					});
				}
			});
		});

		if (
			this.clientBrokerage &&
			(this.clientBrokerage?.name?.toLowerCase().includes(lowerSearchTerm) ||
				lowerSearchTerm.includes('brokerage'))
		) {
			this.contactSearchResults.push({
				name: this.clientBrokerage.name,
				association: 'Client Brokerage',
				type: ContactType.BROKERAGE,
			});
		}

		if (this.clientBrokerage?.broker) {
			const name = `${this.clientBrokerage?.broker?.firstName} ${this.clientBrokerage?.broker?.lastName}`;
			if (
				name.toLowerCase().includes(lowerSearchTerm) ||
				lowerSearchTerm.includes('broker')
			) {
				this.contactSearchResults.push({
					name,
					association: 'Client Broker',
					type: ContactType.BROKERAGE,
				});
			}
		}

		if (
			this.otherPartyBrokerage &&
			(this.otherPartyBrokerage?.name
				?.toLowerCase()
				.includes(lowerSearchTerm) ||
				lowerSearchTerm.includes('brokerage'))
		) {
			this.contactSearchResults.push({
				name: this.otherPartyBrokerage.name,
				association: 'Other party Brokerage',
				type: ContactType.OTHER_PARTY_BROKERAGE,
			});
		}

		if (this.otherPartyBrokerage?.broker) {
			const name = `${this.otherPartyBrokerage?.broker?.firstName} ${this.otherPartyBrokerage?.broker?.lastName}`;
			if (
				name.toLowerCase().includes(lowerSearchTerm) ||
				lowerSearchTerm.includes('broker')
			) {
				this.contactSearchResults.push({
					name,
					association: 'Other Party Broker',
					type: ContactType.OTHER_PARTY_BROKERAGE,
				});
			}
		}
	}

	onSelect(
		result: DropdownResult<PageSearchElement | ContactsSearchElement>,
	): void {
		if (result?.groupType === SelectOption.PAGE) {
			this.navService.goToUrl((result.result as PageSearchElement).url);
		} else if (result?.groupType === SelectOption.CONTACT) {
			const _result = result.result as ContactsSearchElement;
			if (_result.type === ContactType.CLIENT) {
				this.drawerService.openDrawer(GlobalProfileComponent, {
					title: 'Global Profile',
					data: { clientId: _result.id },
				});
			} else if (_result.type === ContactType.BROKERAGE) {
				this.navService.goToMatter_Commission(this.matterId, this.fileType);
			} else if (
				_result.type === ContactType.OTHER_PARTY_BROKERAGE ||
				_result.type === ContactType.OTHER_PARTY_LAWFIRM
			) {
				this.navService.goToMatter_Parties_OtherParties(this.matterId);
			}
		}
	}

	matchesName(
		searchValue: string,
		client: PersonClient | BusinessClient,
	): boolean {
		return (
			(client as PersonClient)?.firstName
				?.toLowerCase()
				.includes(searchValue) ||
			(client as PersonClient)?.middleNames
				?.toLowerCase()
				.includes(searchValue) ||
			(client as PersonClient)?.lastName?.toLowerCase().includes(searchValue) ||
			(client as BusinessClient)?.name?.toLowerCase().includes(searchValue)
		);
	}

	getUrl(page: Page): string {
		switch (page) {
			case Page.TRUST_LEDGER:
				return UrlService.Matter_Ledger(this.matterId);
			case Page.BILLS_DEBTS:
				return UrlService.Matter_Debts(this.matterId);
			case Page.COMMISSION:
				if (this.fileType === MatterFileType.PURCHASE) {
					return UrlService.Matter_Commission_Buyer_Brokerage(this.matterId);
				} else {
					return UrlService.Matter_Commission_Seller_Brokerage(this.matterId);
				}
			case Page.COMPLIANCE:
				return UrlService.Matter_Property_ComplianceLetters(this.matterId);
			case Page.CONDO_TAB:
				return UrlService.Matter_Property_CondoCorp(this.matterId);
			case Page.FIRE_INSURANCE:
				return UrlService.Matter_Property_FireInsurance(this.matterId);
			case Page.OTHER_PARTIES:
				return UrlService.Matter_Parties_OtherParties(this.matterId);
			case Page.OVERVIEW:
				return UrlService.Matter(this.matterId);
			case Page.PARTIES:
				return UrlService.Matter_Parties(this.matterId);
			case Page.PROPERTY_DETAILS:
				return UrlService.Matter_Property_Details(this.matterId);
			case Page.TITLE_INSURANCE:
				return UrlService.Matter_TitleInsurance(this.matterId);
			case Page.ADJUSTMENTS:
				return UrlService.Matter_Adjustments(this.matterId);
			case Page.ACCOUNT:
				return UrlService.Matter_Accounts(this.matterId);
			case Page.HOLDBACKS:
				return UrlService.Matter_Holdbacks(this.matterId);
			case Page.REDIRECTION:
				return UrlService.Matter_Redirection(this.matterId);
			case Page.DOCUMENTS:
				return UrlService.Matter_Documents_SaleDocs(this.matterId);
			case Page.RESPONSE_TO_REQUISITIONS:
				return UrlService.Matter_Documents_Response_To_Requisitions(
					this.matterId,
				);
			case Page.UNDERTAKINGS:
				return UrlService.Undertakings_Given(this.matterId);
			case Page.ENCUMBRANCE:
				return UrlService.Matter_Encumbrances(this.matterId);
		}
	}
}
